import React, { Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';

// Helper to preserve query parameters and URL fragments on route redirection
const RedirectWithQuery = ({ to }) => {
  const location = useLocation();
  if (location.pathname === to) {
    return null;
  }
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
};

// Persistent Admin Layout component to prevent unmounting/remounting
const AdminLayout = () => {
  return (
    <div className="lms-wrapper lms-layout-wrapper">
      <Header isLayout={true} />
      <div className="lms-main-grid lms-layout-grid">
        <Sidebar isLayout={true} />
        <Outlet />
      </div>
    </div>
  );
};

// Persistent Employee Layout component to prevent unmounting/remounting
const EmployeeLayout = () => {
  return (
    <div className="lms-wrapper lms-layout-wrapper">
      <Header isLayout={true} />
      <div className="lms-main-grid lms-layout-grid">
        <Sidebar isLayout={true} />
        <Outlet />
      </div>
    </div>
  );
};

// ── Eager-loaded (small, needed immediately) ──────────────────────────────────
// Public Landing Pages — eager for fast first paint (SEO & user experience)
import Home from '../pages/public/Home';
import Programs from '../pages/public/Programs';
import YogaTTC500 from '../pages/public/YogaTTC500';
import YogaTTC300 from '../pages/public/YogaTTC300';
import YogaTTC200 from '../pages/public/YogaTTC200';
import DietNutrition from '../pages/public/DietNutrition';
import FaceYoga from '../pages/public/FaceYoga';
import KidsYoga from '../pages/public/KidsYoga';
import PCOSManagementProgram from '../pages/public/PCOSManagementProgram';
import EnrollConfirm from '../pages/public/EnrollConfirm';
import TeacherApply from '../pages/public/TeacherApply';
import PrivacyPolicy from '../pages/public/PrivacyPolicy';
import TermsAndConditions from '../pages/public/TermsAndConditions';
import DataDeletion from '../pages/public/DataDeletion';
import AboutUs from '../pages/public/AboutUs';
import ContactUs from '../pages/public/ContactUs';
import HydrabadCenter from '../pages/public/HydrabadCenter';
import HydrabadFaceYoga from '../pages/public/HydrabadFaceYoga';

// Auth — eager (small and critical for redirect flow)
import Login from '../pages/auth/Login';
import ForgotPassword from '../pages/auth/ForgotPassword';

// ── Lazy-loaded Admin pages ───────────────────────────────────────────────────
const AdminDashboard               = React.lazy(() => import('../pages/admin/AdminDashboard'));
const AdminProfile                 = React.lazy(() => import('../pages/admin/AdminProfile'));
const ManageCourses                = React.lazy(() => import('../pages/admin/ManageCourses'));
const ManageCourseModules         = React.lazy(() => import('../pages/admin/ManageCourseModules'));
const ManageCoupons                = React.lazy(() => import('../pages/admin/ManageCoupons'));
const ManageBatches                = React.lazy(() => import('../pages/admin/ManageBatches'));
const ManageStudents               = React.lazy(() => import('../pages/admin/ManageStudents'));
const StudentDetails               = React.lazy(() => import('../pages/admin/StudentDetails'));
const InterestedStudents           = React.lazy(() => import('../pages/admin/InterestedStudents'));
const ManageEmployees              = React.lazy(() => import('../pages/admin/ManageEmployees'));
const EmployeeDetails              = React.lazy(() => import('../pages/admin/EmployeeDetails'));
const StaffActivities              = React.lazy(() => import('../pages/admin/StaffActivities'));
const ManageLectures               = React.lazy(() => import('../pages/admin/ManageLectures'));
const ReviewBatchLectures          = React.lazy(() => import('../pages/admin/ReviewBatchLectures'));
const ManageCommonCourses          = React.lazy(() => import('../pages/admin/ManageCommonCourses'));
const Reports                      = React.lazy(() => import('../pages/admin/Reports'));
const Settings                     = React.lazy(() => import('../pages/admin/Settings'));
const QuestionSettings             = React.lazy(() => import('../pages/admin/QuestionSettings'));
const QuestionBank                 = React.lazy(() => import('../pages/admin/QuestionBank'));
const ReviewQuestions              = React.lazy(() => import('../pages/admin/ReviewQuestions'));
const ExamReviews                  = React.lazy(() => import('../pages/admin/ExamReviews'));
const ExamReviewDetails            = React.lazy(() => import('../pages/admin/ExamReviewDetails'));
const AdminExamResults             = React.lazy(() => import('../pages/admin/AdminExamResults'));
const FeeDue                       = React.lazy(() => import('../pages/admin/FeeDue'));
const TotalFeeCollected            = React.lazy(() => import('../pages/admin/TotalFeeCollected'));
const TeacherApplicationsList      = React.lazy(() => import('../pages/admin/TeacherApplicationsList'));
const TeacherApplicationDetails    = React.lazy(() => import('../pages/admin/TeacherApplicationDetails'));
const EmployeeTeacherDetails       = React.lazy(() => import('../pages/admin/EmployeeTeacherDetails'));
const TeacherReviewsList           = React.lazy(() => import('../pages/admin/TeacherReviewsList'));
const TeacherReviewDetails         = React.lazy(() => import('../pages/admin/TeacherReviewDetails'));
const TeacherProfileUpdateRequests = React.lazy(() => import('../pages/admin/TeacherProfileUpdateRequests'));
const FacultyPayroll               = React.lazy(() => import('../pages/admin/FacultyPayroll'));
const CentreCourseEnquiries        = React.lazy(() => import('../pages/admin/CentreCourseEnquiries'));

// Demo Class System Pages
const ManageDemoStudents = React.lazy(() => import('../pages/admin/ManageDemoStudents'));
const ManageDemoCourses  = React.lazy(() => import('../pages/admin/ManageDemoCourses'));
const ManageDemoBatches  = React.lazy(() => import('../pages/admin/ManageDemoBatches'));
const DemoLogin          = React.lazy(() => import('../pages/auth/DemoLogin'));
const DemoDashboard      = React.lazy(() => import('../pages/student/DemoDashboard'));
const DemoClassLanding   = React.lazy(() => import('../pages/public/DemoClassLanding'));
const DemoClassroom      = React.lazy(() => import('../pages/student/DemoClassroom'));
const DemoProfile        = React.lazy(() => import('../pages/student/DemoProfile'));
const DemoUpgradePage    = React.lazy(() => import('../pages/student/DemoUpgradePage'));
const ManageDemoVideos   = React.lazy(() => import('../pages/employee/ManageDemoVideos'));
const AddDemoVideoForm   = React.lazy(() => import('../pages/employee/AddDemoVideoForm'));
const ReviewDemoVideos   = React.lazy(() => import('../pages/admin/ReviewDemoVideos'));
const ManageDemoVideosAdmin = React.lazy(() => import('../pages/admin/ManageDemoVideosAdmin'));
const AdminCommunityMonitor = React.lazy(() => import('../pages/admin/AdminCommunityMonitor'));

// ── Lazy-loaded Employee pages ────────────────────────────────────────────────
const EmployeeDashboard    = React.lazy(() => import('../pages/employee/EmployeeDashboard'));
const TeacherMyBatches     = React.lazy(() => import('../pages/employee/TeacherMyBatches'));
const TeacherCommunity     = React.lazy(() => import('../pages/employee/TeacherCommunity'));
const TeacherCertificates  = React.lazy(() => import('../pages/employee/TeacherCertificates'));
const TeacherPayments      = React.lazy(() => import('../pages/employee/TeacherPayments'));
const MyAssignedBatches    = React.lazy(() => import('../pages/employee/MyAssignedBatches'));
const AddDailyLecture      = React.lazy(() => import('../pages/employee/AddDailyLecture'));
const ManageMyLectures     = React.lazy(() => import('../pages/employee/ManageMyLectures'));
const EmployeeProfile      = React.lazy(() => import('../pages/employee/EmployeeProfile'));
const TeacherStudents      = React.lazy(() => import('../pages/employee/TeacherStudents'));
const TeacherAddQuestion   = React.lazy(() => import('../pages/employee/TeacherAddQuestion'));
const TeacherQuestionBank  = React.lazy(() => import('../pages/employee/TeacherQuestionBank'));
const TeacherCreateExam    = React.lazy(() => import('../pages/employee/TeacherCreateExam'));
const TeacherExamsList     = React.lazy(() => import('../pages/employee/TeacherExamsList'));

// ── Lazy-loaded Student pages ─────────────────────────────────────────────────
const StudentDashboard     = React.lazy(() => import('../pages/student/StudentDashboard'));
const StudentCommunity     = React.lazy(() => import('../pages/student/StudentCommunity'));
const MyBatchLectures      = React.lazy(() => import('../pages/student/MyBatchLectures'));
const BatchLecturePage     = React.lazy(() => import('../pages/student/BatchLecturePage'));
const StudentLectureDetail = React.lazy(() => import('../pages/student/StudentLectureDetail'));
const CommonCourses        = React.lazy(() => import('../pages/student/CommonCourses'));
const CommonCourseVideos   = React.lazy(() => import('../pages/student/CommonCourseVideos'));
const StudentProfile       = React.lazy(() => import('../pages/student/StudentProfile'));
const StudentFees          = React.lazy(() => import('../pages/student/StudentFees'));
const StudentExams         = React.lazy(() => import('../pages/student/StudentExams'));
const StudentExamAttempt   = React.lazy(() => import('../pages/student/StudentExamAttempt'));
const StudentExamResult    = React.lazy(() => import('../pages/student/StudentExamResult'));
const StudentCertificates  = React.lazy(() => import('../pages/student/StudentCertificates'));
const TeacherProfileView   = React.lazy(() => import('../pages/student/TeacherProfileView'));

// ── Shared Community Details Pages ──────────────────────────────────────────────
const CommunityQuestionDetails = React.lazy(() => import('../pages/community/CommunityQuestionDetails'));
const CommunityAnnouncementDetails = React.lazy(() => import('../pages/community/CommunityAnnouncementDetails'));

// Suspense fallback — minimal so it doesn't flash an ugly spinner over the layout
const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <div style={{ textAlign: 'center', color: '#6b7280' }}>
      <div style={{
        width: 40, height: 40, margin: '0 auto 12px',
        border: '3px solid #e5e7eb',
        borderTopColor: '#4f46e5',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ fontSize: 14, margin: 0 }}>Loading…</p>
    </div>
  </div>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Landing Pages & Courses */}
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/courses" element={<Programs />} />
        <Route path="/courses.html" element={<Navigate to="/courses" replace />} />
        <Route path="/programs" element={<Navigate to="/courses" replace />} />
        <Route path="/programs.html" element={<Navigate to="/courses" replace />} />

        {/* Individual Course Detail Pages under /courses/ */}
        <Route path="/courses/200-hour-yoga-teacher-training-rishikesh" element={<YogaTTC200 />} />
        <Route path="/courses/200-hour-yoga-teacher-training-rishikesh.html" element={<Navigate to="/courses/200-hour-yoga-teacher-training-rishikesh" replace />} />
        <Route path="/200-hour-yoga-teacher-training-rishikesh" element={<Navigate to="/courses/200-hour-yoga-teacher-training-rishikesh" replace />} />
        <Route path="/200-hour-yoga-teacher-training-rishikesh.html" element={<Navigate to="/courses/200-hour-yoga-teacher-training-rishikesh" replace />} />

        <Route path="/courses/300hr-yoga-ttc" element={<YogaTTC300 />} />
        <Route path="/courses/300hr-yoga-ttc.html" element={<Navigate to="/courses/300hr-yoga-ttc" replace />} />
        <Route path="/300hr-yoga-ttc" element={<Navigate to="/courses/300hr-yoga-ttc" replace />} />
        <Route path="/300hr-yoga-ttc.html" element={<Navigate to="/courses/300hr-yoga-ttc" replace />} />

        <Route path="/courses/500hr-yoga-ttc" element={<YogaTTC500 />} />
        <Route path="/courses/500hr-yoga-ttc.html" element={<Navigate to="/courses/500hr-yoga-ttc" replace />} />
        <Route path="/500hr-yoga-ttc" element={<Navigate to="/courses/500hr-yoga-ttc" replace />} />
        <Route path="/500hr-yoga-ttc.html" element={<Navigate to="/courses/500hr-yoga-ttc" replace />} />

        <Route path="/courses/diet-and-nutrition-course-online" element={<DietNutrition />} />
        <Route path="/courses/diet-and-nutrition-course-online.html" element={<Navigate to="/courses/diet-and-nutrition-course-online" replace />} />
        <Route path="/diet-and-nutrition-course-online" element={<Navigate to="/courses/diet-and-nutrition-course-online" replace />} />
        <Route path="/diet-and-nutrition-course-online.html" element={<Navigate to="/courses/diet-and-nutrition-course-online" replace />} />

        <Route path="/courses/face-yoga-teacher-training-course-online" element={<FaceYoga />} />
        <Route path="/courses/face-yoga-teacher-training-course-online.html" element={<Navigate to="/courses/face-yoga-teacher-training-course-online" replace />} />
        <Route path="/face-yoga-teacher-training-course-online" element={<Navigate to="/courses/face-yoga-teacher-training-course-online" replace />} />
        <Route path="/face-yoga-teacher-training-course-online.html" element={<Navigate to="/courses/face-yoga-teacher-training-course-online" replace />} />

        <Route path="/courses/95-hour-kids-yoga-teacher-training" element={<KidsYoga />} />
        <Route path="/courses/95-hour-kids-yoga-teacher-training.html" element={<Navigate to="/courses/95-hour-kids-yoga-teacher-training" replace />} />
        <Route path="/95-hour-kids-yoga-teacher-training" element={<Navigate to="/courses/95-hour-kids-yoga-teacher-training" replace />} />
        <Route path="/95-hour-kids-yoga-teacher-training.html" element={<Navigate to="/courses/95-hour-kids-yoga-teacher-training" replace />} />
        <Route path="/kids-yoga-teacher-training" element={<Navigate to="/courses/95-hour-kids-yoga-teacher-training" replace />} />
        <Route path="/kids-yoga-teacher-training.html" element={<Navigate to="/courses/95-hour-kids-yoga-teacher-training" replace />} />

        <Route path="/courses/pcos-pcod-management-program" element={<PCOSManagementProgram />} />
        <Route path="/courses/pcos-pcod-management-program.html" element={<Navigate to="/courses/pcos-pcod-management-program" replace />} />
        <Route path="/pcos-pcod-management-program" element={<Navigate to="/courses/pcos-pcod-management-program" replace />} />
        <Route path="/pcos-pcod-management-program.html" element={<Navigate to="/courses/pcos-pcod-management-program" replace />} />
        <Route path="/course/pcos-pcod-management-program" element={<Navigate to="/courses/pcos-pcod-management-program" replace />} />
        <Route path="/course/pcos-pcod-management-program.html" element={<Navigate to="/courses/pcos-pcod-management-program" replace />} />

        <Route path="/enroll-confirm" element={<EnrollConfirm />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
        {/* Hyderabad Centre Primary Routes */}
        <Route path="/Centre/hydrabad" caseSensitive element={<HydrabadCenter />} />
        <Route path="/Centre/hydrabad/face-yoga-teacher-training" caseSensitive element={<HydrabadFaceYoga />} />

        {/* Hyderabad Centre Backward-Compatibility Redirects (Preserves query params and hash fragments) */}
        <Route path="/center/hydrabad" element={<RedirectWithQuery to="/Centre/hydrabad" />} />
        <Route path="/center/hydrabad.html" element={<RedirectWithQuery to="/Centre/hydrabad" />} />
        <Route path="/centre/hydrabad" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad" />} />
        <Route path="/centre/hydrabad.html" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad" />} />
        <Route path="/Center/hydrabad" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad" />} />
        <Route path="/Center/hydrabad.html" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad" />} />
        <Route path="/Centre/hydrabad.html" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad" />} />

        <Route path="/center/hydrabad/face-yoga-teacher-training" element={<RedirectWithQuery to="/Centre/hydrabad/face-yoga-teacher-training" />} />
        <Route path="/center/hydrabad/face-yoga-teacher-training.html" element={<RedirectWithQuery to="/Centre/hydrabad/face-yoga-teacher-training" />} />
        <Route path="/centre/hydrabad/face-yoga-teacher-training" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad/face-yoga-teacher-training" />} />
        <Route path="/centre/hydrabad/face-yoga-teacher-training.html" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad/face-yoga-teacher-training" />} />
        <Route path="/Center/hydrabad/face-yoga-teacher-training" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad/face-yoga-teacher-training" />} />
        <Route path="/Center/hydrabad/face-yoga-teacher-training.html" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad/face-yoga-teacher-training" />} />
        <Route path="/Centre/hydrabad/face-yoga-teacher-training.html" caseSensitive element={<RedirectWithQuery to="/Centre/hydrabad/face-yoga-teacher-training" />} />
        <Route path="/teacher-apply" element={<TeacherApply />} />
        <Route path="/teach-with-us" element={<TeacherApply />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy.html" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/terms-and-conditions.html" element={<TermsAndConditions />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/data-deletion.html" element={<DataDeletion />} />

        {/* Portal Auth Route */}
        <Route path="/login" element={<Login />} />
        <Route path="/portal-login.html" element={<Navigate to="/login" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/Demo_login" element={<Navigate to="/login?account=demo" replace />} />
        <Route path="/demo_login" element={<Navigate to="/login?account=demo" replace />} />
        <Route path="/demo-register" element={<Navigate to="/courses" replace />} />
        <Route path="/demo-class-registration" element={<Navigate to="/courses" replace />} />
        <Route path="/demo-class" element={<DemoClassLanding />} />

        {/* Legacy /my-lms/ route redirects */}
        <Route path="/my-lms/login" element={<Navigate to="/login" replace />} />
        <Route path="/my-lms/student/dashboard" element={<Navigate to="/student/dashboard" replace />} />
        <Route path="/my-lms/employee/dashboard" element={<Navigate to="/employee/dashboard" replace />} />
        <Route path="/my-lms/employee/community" element={<Navigate to="/employee/community" replace />} />
        <Route path="/my-lms/admin/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/my-lms/demo/dashboard" element={<Navigate to="/demo/dashboard" replace />} />

        {/* Admin Routes with persistent Layout */}
        <Route element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout /></ProtectedRoute>}>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/profile" element={<AdminProfile />} />
          <Route path="/admin/courses" element={<ManageCourses />} />
          <Route path="/admin/course-modules" element={<ManageCourseModules />} />
          <Route path="/admin/coupons" element={<ManageCoupons />} />
          <Route path="/admin/batches" element={<ManageBatches />} />
          <Route path="/admin/students" element={<ManageStudents />} />
          <Route path="/admin/students/:id" element={<StudentDetails />} />
          <Route path="/admin/fee-due" element={<FeeDue />} />
          <Route path="/admin/total-fee-collected" element={<TotalFeeCollected />} />
          <Route path="/admin/faculty-payroll" element={<FacultyPayroll />} />
          <Route path="/admin/course-enquiries" element={<InterestedStudents />} />
          <Route path="/admin/interested-students" element={<InterestedStudents />} />
          <Route path="/admin/centre-course-enquiries" element={<CentreCourseEnquiries />} />
          <Route path="/admin/employees" element={<ManageEmployees />} />
          <Route path="/admin/employees/:id" element={<EmployeeDetails />} />
          <Route path="/admin/employees/:id/teacher-details" element={<EmployeeTeacherDetails />} />
          <Route path="/admin/teacher-applications" element={<TeacherApplicationsList />} />
          <Route path="/admin/teacher-applications/:id" element={<TeacherApplicationDetails />} />
          <Route path="/admin/teacher-reviews" element={<TeacherReviewsList />} />
          <Route path="/admin/teacher-reviews/:id" element={<TeacherReviewDetails />} />
          <Route path="/admin/teacher-profile-updates" element={<TeacherProfileUpdateRequests />} />
          <Route path="/admin/staff-activities" element={<StaffActivities />} />
          <Route path="/admin/lectures" element={<ManageLectures />} />
          <Route path="/admin/review-batch-lectures" element={<ReviewBatchLectures />} />
          <Route path="/admin/review-demo-videos" element={<ReviewDemoVideos />} />
          <Route path="/admin/common-courses" element={<ManageCommonCourses />} />
          <Route path="/admin/reports" element={<Reports />} />
          <Route path="/admin/settings" element={<Settings />} />
          <Route path="/admin/question-categories" element={<QuestionSettings />} />
          <Route path="/admin/question-bank" element={<QuestionBank />} />
          <Route path="/admin/review-questions" element={<ReviewQuestions />} />
          <Route path="/admin/exams-review" element={<ExamReviews />} />
          <Route path="/admin/exams-review/:examId" element={<ExamReviewDetails />} />
          <Route path="/admin/exam-results" element={<AdminExamResults />} />
          <Route path="/admin/demo-students" element={<ManageDemoStudents />} />
          <Route path="/admin/demo-courses" element={<ManageDemoCourses />} />
          <Route path="/admin/demo-batches" element={<ManageDemoBatches />} />
          <Route path="/admin/demo-videos" element={<ManageDemoVideosAdmin />} />
          <Route path="/admin/community" element={<AdminCommunityMonitor />} />
          <Route path="/admin/community/question/:questionId" element={<CommunityQuestionDetails role="admin" />} />
          <Route path="/admin/community/announcement/:announcementId" element={<CommunityAnnouncementDetails role="admin" />} />
        </Route>

        {/* Employee Routes with persistent Layout */}
        <Route element={<ProtectedRoute allowedRoles={['employee', 'teacher']}><EmployeeLayout /></ProtectedRoute>}>
          <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
          <Route path="/employee/my-batches" element={<TeacherMyBatches />} />
          <Route path="/employee/community" element={<TeacherCommunity />} />
          <Route path="/employee/community/question/:questionId" element={<CommunityQuestionDetails role="employee" />} />
          <Route path="/employee/community/announcement/:announcementId" element={<CommunityAnnouncementDetails role="employee" />} />
          <Route path="/employee/certificates" element={<TeacherCertificates />} />
          <Route path="/employee/payments" element={<TeacherPayments />} />
          <Route path="/employee/batches" element={<MyAssignedBatches />} />
          <Route path="/employee/add-lecture" element={<AddDailyLecture />} />
          <Route path="/employee/my-lectures" element={<ManageMyLectures />} />
          <Route path="/employee/demo-videos" element={<ManageDemoVideos />} />
          <Route path="/employee/add-demo-video" element={<AddDemoVideoForm />} />
          <Route path="/employee/profile" element={<EmployeeProfile />} />
          <Route path="/employee/students" element={<TeacherStudents />} />
          <Route path="/employee/add-question" element={<TeacherAddQuestion />} />
          <Route path="/employee/my-question-bank" element={<TeacherQuestionBank />} />
          <Route path="/employee/create-exam" element={<TeacherCreateExam />} />
          <Route path="/employee/exams" element={<TeacherExamsList />} />
        </Route>

        {/* Student Routes */}
        <Route path="/student/dashboard" element={<ProtectedRoute allowedRoles={['student']}><StudentDashboard /></ProtectedRoute>} />
        <Route path="/student/community" element={<ProtectedRoute allowedRoles={['student']}><StudentCommunity /></ProtectedRoute>} />
        <Route path="/student/community/question/:questionId" element={<ProtectedRoute allowedRoles={['student']}><CommunityQuestionDetails role="student" /></ProtectedRoute>} />
        <Route path="/student/community/announcement/:announcementId" element={<ProtectedRoute allowedRoles={['student']}><CommunityAnnouncementDetails role="student" /></ProtectedRoute>} />
        <Route path="/student/batch-lectures" element={<ProtectedRoute allowedRoles={['student']}><MyBatchLectures /></ProtectedRoute>} />
        <Route path="/student/batch-lectures/:batchId" element={<ProtectedRoute allowedRoles={['student']}><BatchLecturePage /></ProtectedRoute>} />
        <Route path="/student/batch-lectures/:batchId/lecture/:lectureId" element={<ProtectedRoute allowedRoles={['student']}><StudentLectureDetail /></ProtectedRoute>} />
        <Route path="/student/common-courses" element={<ProtectedRoute allowedRoles={['student']}><CommonCourses /></ProtectedRoute>} />
        <Route path="/student/common-courses/:courseId" element={<ProtectedRoute allowedRoles={['student']}><CommonCourseVideos /></ProtectedRoute>} />
        <Route path="/student/profile" element={<ProtectedRoute allowedRoles={['student']}><StudentProfile /></ProtectedRoute>} />
        <Route path="/student/fees" element={<ProtectedRoute allowedRoles={['student']}><StudentFees /></ProtectedRoute>} />
        <Route path="/student/exams" element={<ProtectedRoute allowedRoles={['student']}><StudentExams /></ProtectedRoute>} />
        <Route path="/student/certificates" element={<ProtectedRoute allowedRoles={['student']}><StudentCertificates /></ProtectedRoute>} />
        <Route path="/student/teachers/:teacherId" element={<ProtectedRoute allowedRoles={['student']}><TeacherProfileView /></ProtectedRoute>} />
        <Route path="/student/exams/:id/attempt/:attemptId" element={<ProtectedRoute allowedRoles={['student']}><StudentExamAttempt /></ProtectedRoute>} />
        <Route path="/student/exams/:id/result/:attemptId" element={<ProtectedRoute allowedRoles={['student']}><StudentExamResult /></ProtectedRoute>} />

        {/* Demo Student Routes */}
        <Route path="/demo/dashboard" element={<ProtectedRoute allowedRoles={['demo_student']}><DemoDashboard /></ProtectedRoute>} />
        <Route path="/demo/classroom" element={<ProtectedRoute allowedRoles={['demo_student']}><DemoClassroom /></ProtectedRoute>} />
        <Route path="/demo/teachers/:teacherId" element={<ProtectedRoute allowedRoles={['demo_student']}><TeacherProfileView /></ProtectedRoute>} />
        <Route path="/demo/profile" element={<ProtectedRoute allowedRoles={['demo_student']}><DemoProfile /></ProtectedRoute>} />
        <Route path="/demo/fees" element={<ProtectedRoute allowedRoles={['demo_student']}><DemoUpgradePage feature="Fee Ledger" /></ProtectedRoute>} />
        <Route path="/demo/exams" element={<ProtectedRoute allowedRoles={['demo_student']}><DemoUpgradePage feature="Exams & Assessments" /></ProtectedRoute>} />
        <Route path="/demo/certificates" element={<ProtectedRoute allowedRoles={['demo_student']}><DemoUpgradePage feature="Certificates" /></ProtectedRoute>} />
        <Route path="/demo/common-courses" element={<ProtectedRoute allowedRoles={['demo_student']}><DemoUpgradePage feature="Study Materials" /></ProtectedRoute>} />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
